/**
 * The bar over "try it first": says this is a preview of what the recipient
 * will see, and takes the creator back to editing. It sits above the player
 * instead of floating over it, in the chassis style, tinted with the theme.
 * The way back is the same round close button as the creator's header, so it
 * reads as part of the creator rather than as a separate call to action.
 */
import { useTranslation } from "react-i18next";
import Icon from "./Icon";
import { C, FONT, SHADOW, TINT } from "./tokens";

export const PREVIEW_BAR_HEIGHT = 64;

export default function PreviewBar({ name, onBack, dark = false }) {
  const { t: tr } = useTranslation();
  return (
    <div
      role="region"
      aria-label={tr("app.creator.playthrough.label")}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 60,
        paddingTop: "env(safe-area-inset-top, 0px)",
        background: TINT.glass(0.92), backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
        borderBottom: `1px solid ${C.line}`, boxShadow: `0 10px 24px -18px ${TINT.shadow(0.5)}`,
        fontFamily: FONT,
      }}
    >
      <div style={{ height: PREVIEW_BAR_HEIGHT, boxSizing: "border-box", maxWidth: 560, margin: "0 auto", padding: "0 16px 0 20px", display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ flexGrow: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C.accent }}>
            {tr("app.creator.playthrough.label")}
          </span>
          <span style={{ display: "block", marginTop: 1, fontSize: 14, fontWeight: 700, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {name ? tr("app.creator.playthrough.sub", { name }) : tr("app.creator.playthrough.subPlain")}
          </span>
        </span>
        <button
          type="button"
          data-testid="creator-playthrough-close"
          aria-label={tr("app.creator.playthrough.back")}
          title={tr("app.creator.playthrough.back")}
          onClick={onBack}
          style={{
            flexShrink: 0, width: 44, height: 44, borderRadius: 999, boxSizing: "border-box",
            border: dark ? `1.5px solid ${C.dash}` : "none", background: C.surface, boxShadow: SHADOW.raised,
            display: "grid", placeItems: "center", cursor: "pointer", padding: 0,
          }}
        >
          <Icon name="close" size={16} color={C.ink} strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
}
